// Pure calendar/date math for the datetime picker family.
// Ported from bookcars-2.0 `frontend/app/src/shared/utils/calendar.ts` and trimmed:
// no availability blocks, no range fields — this feature needs single-day cells with
// current-month / today / min-max-disabled flags only. No date library.

export interface DayCell {
  /** The concrete day this cell represents. */
  date: Date;
  /** Day-of-month number, for rendering. */
  dayNumber: number;
  /** false for the leading/trailing days that belong to an adjacent month. */
  inCurrentMonth: boolean;
  isToday: boolean;
  /** true when the day falls outside [minDate, maxDate]. */
  disabled: boolean;
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Monday-first week, matching the reference grid.
export const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function toYMD(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromYMD(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export interface BuildCalendarOptions {
  minDate?: Date;
  maxDate?: Date;
}

export function buildCalendar(
  year: number,
  month: number,
  options: BuildCalendarOptions = {},
): DayCell[] {
  const { minDate, maxDate } = options;
  const todayYMD = toYMD(new Date());
  const minYMD = minDate ? toYMD(minDate) : null;
  const maxYMD = maxDate ? toYMD(maxDate) : null;

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // getDay(): 0=Sun..6=Sat. Shift so Monday is column 0.
  const startOffset = (firstDay.getDay() + 6) % 7;

  const makeCell = (date: Date, inCurrentMonth: boolean): DayCell => {
    const ymd = toYMD(date);
    const tooEarly = minYMD !== null && ymd < minYMD;
    const tooLate = maxYMD !== null && ymd > maxYMD;
    return {
      date,
      dayNumber: date.getDate(),
      inCurrentMonth,
      isToday: ymd === todayYMD,
      disabled: tooEarly || tooLate,
    };
  };

  const cells: DayCell[] = [];

  // Leading days from the previous month.
  for (let i = startOffset - 1; i >= 0; i--) {
    cells.push(makeCell(new Date(year, month, -i), false));
  }

  // Current month.
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(makeCell(new Date(year, month, d), true));
  }

  // Trailing days from the next month to complete the final week.
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    const trailing = 7 - remainder;
    for (let d = 1; d <= trailing; d++) {
      cells.push(makeCell(new Date(year, month + 1, d), false));
    }
  }

  return cells;
}
